import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {CopyText, Icon} from 'components';
import {AUTH_SOURCE_DESCRIPTION_TRANSLATE} from 'constants/translate';
import {AuthSource} from 'enums';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.INTEGRATIONS;

type IntegrationLabelProps = {
    text: string;
};

const IntegrationLabel: React.FC<IntegrationLabelProps> = ({text}: IntegrationLabelProps) => (
    <span className='tw-text-default'>
        {text}
        {': '}
    </span>
);

type SourceLabelProps = {
    label: string;
};

export const SourceLabel: React.FC<SourceLabelProps> = ({label}: SourceLabelProps) => (
    <h3 className='tw-ts-h5-bold tw-mb-1 tw-text-break-word'>
        {label}
    </h3>
);

type IntegrationCredentialsFieldProps = {
    text: string;
    value: string;
    copyIcon?: boolean;
};

const IntegrationCredentialsField: React.FC<IntegrationCredentialsFieldProps> =
    ({text, value, copyIcon}: IntegrationCredentialsFieldProps) => (
        <div className='tw-mt-2'>
            <IntegrationLabel text={text} />
            {value}
            {copyIcon &&
                <CopyText
                    className='tw-ml-1'
                    iconColor='primary'
                    text={value}
                    textInTooltip={false}
                    iconOnly
                    copiedLabelOnly
                    iconSize='sm'
                />}
        </div>
    );

type ActionsProps = {
    isEditable: boolean;
    onEdit: () => void;
    onDelete: () => void;
};

export const Actions: React.FC<ActionsProps> = ({isEditable, onEdit, onDelete}: ActionsProps) => (
    <div className='tw-flex tw-ml-auto'>
        {isEditable &&
            <Icon
                name='edit'
                button
                group
                color='primary'
                stopPropagation
                onClick={onEdit}
            />}
        <Icon
            name='delete'
            color='alert'
            button
            group
            className='tw-ml-2'
            stopPropagation
            onClick={onDelete}
        />
    </div>
);

export const IntegrationDescription: React.FC<{source: string}> = ({source}: {source: string}) => {
    const {t} = useTranslation();
    return (
        <div className='tw-text-default-dimmed tw-text-sm'>
            {t(AUTH_SOURCE_DESCRIPTION_TRANSLATE[source])}
        </div>
    );
};

type SalesforceIntegrationCredentials = {
    orgId: string;
    userId: string;
    token: string;
}

type SalesforceIntegrationProps = {
    integration: {
        source: AuthSource;
        credentials: SalesforceIntegrationCredentials;
        data: any;
    }
};

const SalesforceIntegration: React.FC<SalesforceIntegrationProps> = ({integration}: SalesforceIntegrationProps) => {
    const {t} = useTranslation();
    return (
        <div className='sm:tw-mr-16'>
            <IntegrationDescription source={integration.source} />
            <IntegrationCredentialsField text={t(T_KEYS.FIELDS.ORG_ID)} value={integration.credentials.orgId} />
            <IntegrationCredentialsField text={t(T_KEYS.FIELDS.USER_ID)} value={integration.credentials.userId} />
            <IntegrationCredentialsField text={t(T_KEYS.FIELDS.TOKEN)} value={integration.credentials.token} copyIcon />
        </div>
    );
};

type DeviceIntegrationCredentials = {
    deviceId: string;
    token: string;
}

type DeviceIntegrationProps = {
    integration: {
        source: AuthSource;
        credentials: DeviceIntegrationCredentials;
        data: any;
    }
};

const DeviceIntegration: React.FC<DeviceIntegrationProps> = ({integration}: DeviceIntegrationProps) => {
    const {t} = useTranslation();
    return (
        <div>
            <IntegrationDescription source={integration.source} />
            <IntegrationCredentialsField text={t(T_KEYS.FIELDS.DEVICE_ID)} value={integration.credentials.deviceId} />
            {integration.data?.device?.manufacturer &&
                <div className='tw-mt-2'>
                    <IntegrationLabel text={t(T_KEYS.FIELDS.DEVICE_MODEL)} />
                    <span>{integration.data?.device?.manufacturer}</span>
                    {integration.data?.device?.model && <span>{` (${integration.data?.device?.model})`}</span>}
                </div>}
        </div>
    );
};

type AppointmentsApplicationIntegrationCredentials = {
    name: string;
    key: string;
    token: string;
}

type AppointmentsApplicationIntegrationProps = {
    integration: {
        source: AuthSource;
        credentials: AppointmentsApplicationIntegrationCredentials;
    }
};

const AppointmentsApplicationIntegration: React.FC<AppointmentsApplicationIntegrationProps> =
    ({integration}: AppointmentsApplicationIntegrationProps) => {
        const {t} = useTranslation();
        return (
            <div className='sm:tw-mr-16'>
                <IntegrationDescription source={integration.source} />
                <IntegrationCredentialsField text={t(T_KEYS.FIELDS.NAME)} value={integration.credentials.name} />
                <IntegrationCredentialsField text={t(T_KEYS.FIELDS.KEY)} value={integration.credentials.key} copyIcon />
                <IntegrationCredentialsField text={t(T_KEYS.FIELDS.TOKEN)} value={integration.credentials.token} copyIcon />
            </div>
        );
    };

type IntegrationContentProps = {
    integration: {
        source: AuthSource;
        credentials: any;
        data: any;
    },
    isSalesforceIntegration: boolean;
    isDeviceIntegration: boolean;
    isAppointmentsIntegration: boolean;
}

export const IntegrationContent: React.FC<IntegrationContentProps> = ({
    integration, isDeviceIntegration, isSalesforceIntegration, isAppointmentsIntegration
}: IntegrationContentProps) => (
    <Fragment>
        {isSalesforceIntegration && <SalesforceIntegration integration={integration} />}
        {isDeviceIntegration && <DeviceIntegration integration={integration} />}
        {isAppointmentsIntegration && <AppointmentsApplicationIntegration integration={integration} />}
    </Fragment>
);